import {
  buildJourneyPageBreakSteps,
  isJourneyPageBreakQuestionType,
  JourneyPageBreakStepKind,
} from "./journeyPageBreakGroupingHelper";
import { journeyQuestionSequenceHelper } from "./questionSequenceHelpers";
import { FormQuestionDto } from "../types/dtos/forms";
import { BaseCommentDto } from "../types/dtos/forms/BaseCommentDto";
import {
  QuestionAnswer,
  QuestionAnswerValue,
  QuestionTasks,
} from "../types/forms";
import FormQuestion from "../types/forms/FormQuestion";
import FormType from "../types/forms/FormType";
import TaskManagementConfigDto from "../types/dtos/forms/TaskManagementConfigDto";

export type JourneyFormStep = {
  questions: FormQuestion[];
  kind: JourneyPageBreakStepKind;
};

export function buildJourneyFormSteps(
  questionDtos: FormQuestionDto[] | null | undefined
): JourneyFormStep[] {
  if (!questionDtos || questionDtos.length === 0) {
    return [];
  }

  const grouping = buildJourneyPageBreakSteps(
    questionDtos.map((q) => ({
      questionId: q.questionId,
      questionType: q.questionType,
      isConditionallyTriggered: q.sequenceMap?.conditionallyTriggered === true,
      hasConditionalOptionBranches:
        q.sequenceMap?.options != null && q.sequenceMap.options.length > 0,
    }))
  );

  return grouping.steps.map((step) => ({
    kind: step.kind,
    questions: step.questionIds.map((id) => {
      const dto = questionDtos.find((q) => q.questionId === id);
      if (!dto) {
        throw new Error(`Missing question dto for step id ${id}`);
      }
      return new FormQuestion(dto);
    }),
  }));
}

export function findStepIndexContainingQuestion(
  steps: JourneyFormStep[],
  questionId: string
): number {
  return steps.findIndex((step) =>
    step.questions.some((q) => q.questionId === questionId)
  );
}

/** Map a sequenceMap next/prev id onto a step; page-break ids resolve to the following real step. */
export function resolveStepIndexForQuestionId(
  steps: JourneyFormStep[],
  questionDtos: FormQuestionDto[],
  questionId: string | null | undefined
): number | null {
  if (!questionId) {
    return null;
  }

  const direct = findStepIndexContainingQuestion(steps, questionId);
  if (direct >= 0) {
    return direct;
  }

  const dtoIndex = questionDtos.findIndex((q) => q.questionId === questionId);
  if (dtoIndex < 0) {
    return null;
  }

  for (let i = dtoIndex + 1; i < questionDtos.length; i++) {
    const candidate = questionDtos[i];
    if (!isJourneyPageBreakQuestionType(candidate.questionType)) {
      const stepIndex = findStepIndexContainingQuestion(
        steps,
        candidate.questionId
      );
      return stepIndex >= 0 ? stepIndex : null;
    }
  }

  return null;
}

/**
 * Landing step for a saved mostRecentQuestionId.
 * Page-break → following step; if none, previous step.
 */
export function resolveLandingStepIndex(
  steps: JourneyFormStep[],
  questionDtos: FormQuestionDto[],
  mostRecentQuestionId: string
): number {
  if (steps.length === 0) {
    return 0;
  }

  const dto = questionDtos.find((q) => q.questionId === mostRecentQuestionId);
  if (!dto) {
    return 0;
  }

  if (isJourneyPageBreakQuestionType(dto.questionType)) {
    const following = resolveStepIndexForQuestionId(
      steps,
      questionDtos,
      mostRecentQuestionId
    );
    if (following != null) {
      return following;
    }

    const dtoIndex = questionDtos.findIndex(
      (q) => q.questionId === mostRecentQuestionId
    );
    for (let i = dtoIndex - 1; i >= 0; i--) {
      const candidate = questionDtos[i];
      if (!isJourneyPageBreakQuestionType(candidate.questionType)) {
        const prev = findStepIndexContainingQuestion(
          steps,
          candidate.questionId
        );
        if (prev >= 0) {
          return prev;
        }
      }
    }
    return 0;
  }

  const containing = findStepIndexContainingQuestion(
    steps,
    mostRecentQuestionId
  );
  return containing >= 0 ? containing : 0;
}

export function isLastJourneyFormStep(
  steps: JourneyFormStep[],
  stepIndex: number
): boolean {
  return steps.length > 0 && stepIndex === steps.length - 1;
}

export function validateJourneyFormStep(
  step: JourneyFormStep,
  getAnswer: (questionId: string) => QuestionAnswerValue | null,
  getTasks: (questionId: string) => QuestionTasks | null,
  getComments: (questionId: string) => BaseCommentDto[],
  loggedInUserId: number,
  formSubjectUserId: number,
  formType: FormType,
  isClientJourneyClosed: boolean,
  ensureAnswerSetGuidId?: (
    config: TaskManagementConfigDto | null | undefined
  ) => void
): boolean {
  return step.questions.every((question) => {
    if (
      ensureAnswerSetGuidId &&
      question.taskManagementConfig != null &&
      question.taskManagementConfig.answerSetGuidId == null
    ) {
      ensureAnswerSetGuidId(question.taskManagementConfig);
    }

    return question.validate(
      getAnswer(question.questionId),
      getTasks(question.questionId)?.tasks ?? null,
      question.taskManagementConfig ?? null,
      getComments(question.questionId),
      loggedInUserId,
      formSubjectUserId,
      formType,
      false,
      isClientJourneyClosed
    ).isValid;
  });
}

/** Next step index within this form, or null when the form is finished. */
export function resolveNextJourneyFormStepIndex(
  steps: JourneyFormStep[],
  questionDtos: FormQuestionDto[],
  currentStepIndex: number,
  currentAnswer: QuestionAnswerValue | null
): number | null {
  if (currentStepIndex < 0 || currentStepIndex >= steps.length) {
    return null;
  }

  const currentStep = steps[currentStepIndex];

  if (currentStep.kind === "GROUPED") {
    const next = currentStepIndex + 1;
    return next < steps.length ? next : null;
  }

  const question = currentStep.questions[0];
  if (!question) {
    return null;
  }

  const nextQuestionId = journeyQuestionSequenceHelper.getNextQuestionId(
    question,
    currentAnswer
  );

  if (!nextQuestionId) {
    return null;
  }

  // Same step (should not happen for sequential length-1) or later step.
  const resolved = resolveStepIndexForQuestionId(
    steps,
    questionDtos,
    nextQuestionId
  );
  if (resolved == null || resolved === currentStepIndex) {
    return null;
  }
  return resolved;
}

/** Previous step index within this form, or null when at the first step. */
export function resolvePreviousJourneyFormStepIndex(
  steps: JourneyFormStep[],
  questionDtos: FormQuestionDto[],
  currentStepIndex: number,
  formAnswers: QuestionAnswer[]
): number | null {
  if (currentStepIndex <= 0 || currentStepIndex >= steps.length) {
    return currentStepIndex > 0 ? currentStepIndex - 1 : null;
  }

  const currentStep = steps[currentStepIndex];

  if (currentStep.kind === "GROUPED") {
    return currentStepIndex - 1;
  }

  const question = currentStep.questions[0];
  if (!question) {
    return currentStepIndex - 1;
  }

  const previousQuestionId = journeyQuestionSequenceHelper.getPreviousQuestionId(
    question,
    questionDtos,
    formAnswers
  );

  if (!previousQuestionId) {
    return null;
  }

  const resolved = resolveStepIndexForQuestionId(
    steps,
    questionDtos,
    previousQuestionId
  );
  if (resolved == null || resolved === currentStepIndex) {
    return currentStepIndex > 0 ? currentStepIndex - 1 : null;
  }
  return resolved;
}

export type JourneyFormStepLocation = {
  formIndex: number;
  stepIndex: number;
};

/** First form that yields at least one renderable journey step. */
export function findFirstFormIndexWithSteps(
  forms: Array<{ questions?: FormQuestionDto[] | null } | null | undefined>
): number {
  if (!forms) {
    return 0;
  }
  for (let i = 0; i < forms.length; i++) {
    if (buildJourneyFormSteps(forms[i]?.questions).length > 0) {
      return i;
    }
  }
  return 0;
}

/** True when any form before currentFormIndex has at least one renderable step. */
export function hasEarlierFormWithSteps(
  forms: Array<{ questions?: FormQuestionDto[] | null } | null | undefined>,
  currentFormIndex: number
): boolean {
  if (!forms || currentFormIndex <= 0) {
    return false;
  }
  for (let i = 0; i < currentFormIndex; i++) {
    if (buildJourneyFormSteps(forms[i]?.questions).length > 0) {
      return true;
    }
  }
  return false;
}

/**
 * Resume location for mostRecentQuestionId.
 * Never activates a form with zero steps (page-break-only).
 */
export function resolveResumeFormAndStep(
  forms: Array<{ questions: FormQuestionDto[] }>,
  mostRecentQuestionId: string
): JourneyFormStepLocation | null {
  if (!forms || forms.length === 0) {
    return null;
  }

  let matchedFormIndex = -1;
  for (let i = 0; i < forms.length; i++) {
    if (
      forms[i].questions?.some((q) => q.questionId === mostRecentQuestionId)
    ) {
      matchedFormIndex = i;
      break;
    }
  }

  if (matchedFormIndex < 0) {
    const first = findFirstFormIndexWithSteps(forms);
    const firstSteps = buildJourneyFormSteps(forms[first]?.questions);
    return firstSteps.length > 0
      ? { formIndex: first, stepIndex: 0 }
      : null;
  }

  const matchedQuestions = forms[matchedFormIndex].questions;
  const matchedSteps = buildJourneyFormSteps(matchedQuestions);
  if (matchedSteps.length > 0) {
    return {
      formIndex: matchedFormIndex,
      stepIndex: resolveLandingStepIndex(
        matchedSteps,
        matchedQuestions,
        mostRecentQuestionId
      ),
    };
  }

  for (let i = matchedFormIndex + 1; i < forms.length; i++) {
    const steps = buildJourneyFormSteps(forms[i].questions);
    if (steps.length > 0) {
      return { formIndex: i, stepIndex: 0 };
    }
  }

  for (let i = matchedFormIndex - 1; i >= 0; i--) {
    const steps = buildJourneyFormSteps(forms[i].questions);
    if (steps.length > 0) {
      return { formIndex: i, stepIndex: steps.length - 1 };
    }
  }

  return null;
}

